import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const ArticleContent = () => {
  const [activeSection, setActiveSection] = useState('introduction');

  const sections = [
    { id: 'introduction', title: 'Introduction', icon: 'BookOpen' },
    { id: 'challenges', title: 'Défis Actuels', icon: 'AlertTriangle' },
    { id: 'solutions', title: 'Solutions Innovantes', icon: 'Lightbulb' },
    { id: 'implementation', title: 'Mise en Œuvre', icon: 'Settings' },
    { id: 'results', title: 'Résultats Attendus', icon: 'TrendingUp' }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Table of Contents - Sticky Sidebar */}
          <div className="lg:col-span-3">
            <div className="sticky top-24 space-y-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Sommaire</h3>
                <nav className="space-y-2">
                  {sections?.map((section) => (
                    <button
                      key={section?.id}
                      onClick={() => setActiveSection(section?.id)}
                      className={`flex items-center space-x-3 w-full p-3 rounded-lg text-left transition-all duration-200 ${
                        activeSection === section?.id
                          ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-600' :'text-gray-600 hover:bg-white hover:text-gray-900'
                      }`}
                    >
                      <Icon name={section?.icon} size={18} />
                      <span className="font-medium">{section?.title}</span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Article Stats */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-4">Impact de l'Article</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Lectures</span>
                    <span className="font-bold text-blue-600">2,847</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Partages</span>
                    <span className="font-bold text-green-600">156</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Note</span>
                    <div className="flex items-center space-x-1">
                      {[1, 2, 3, 4, 5]?.map((star) => (
                        <Icon key={star} name="Star" size={14} className="text-yellow-400 fill-current" />
                      ))}
                      <span className="text-sm font-bold text-gray-700 ml-1">4.8</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9 space-y-12">
            {/* Introduction */}
            <div id="introduction" className="space-y-6">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon name="BookOpen" size={24} className="text-blue-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Introduction : L'Impératif de l'Adaptation</h2>
              </div>
              
              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                <p className="text-xl text-gray-800 font-medium mb-6">
                  Dans un monde où l'intelligence artificielle redéfinit les paradigmes industriels, 
                  le développement des compétences devient l'élément différenciateur crucial pour les organisations qui aspirent à l'excellence.
                </p>
                
                <p className="mb-6">
                  L'industrie automobile, en particulier, traverse une mutation sans précédent. Les véhicules connectés, 
                  l'électrification massive, et l'autonomisation des systèmes exigent une main-d'œuvre dotée de compétences 
                  hybrides alliant expertise technique traditionnelle et maîtrise des technologies émergentes.
                </p>

                <p className="mb-6">
                  Cette transformation ne se limite pas aux aspects techniques. Elle englobe également les soft skills, 
                  la capacité d'adaptation, et surtout, l'aptitude à apprendre en continu dans un environnement en perpétuelle évolution.
                </p>
              </div>

              {/* Key Statistics */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 my-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Chiffres Clés de la Transformation</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">85%</div>
                    <div className="text-sm text-gray-600">des emplois de 2030 n'existent pas encore</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">40%</div>
                    <div className="text-sm text-gray-600">des compétences actuelles seront obsolètes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600 mb-2">2.3x</div>
                    <div className="text-sm text-gray-600">plus rapide, la vitesse d'évolution technologique</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Challenges */}
            <div id="challenges" className="space-y-6">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Icon name="AlertTriangle" size={24} className="text-red-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Les Défis Contemporains du Développement des Compétences</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-gray-900">Défis Organisationnels</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <Icon name="X" size={16} className="text-red-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">Inadéquation entre compétences disponibles et besoins futurs</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Icon name="X" size={16} className="text-red-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">Résistance au changement des équipes établies</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Icon name="X" size={16} className="text-red-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">Manque de visibilité sur les compétences stratégiques</span>
                    </li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-gray-900">Défis Technologiques</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <Icon name="X" size={16} className="text-red-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">Complexité croissante des systèmes intégrés</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Icon name="X" size={16} className="text-red-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">Obsolescence rapide des connaissances techniques</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Icon name="X" size={16} className="text-red-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">Intégration difficile de l'IA dans les processus existants</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertTriangle" size={20} className="text-yellow-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-lg font-semibold text-yellow-800 mb-2">Point d'Attention Critique</h4>
                    <p className="text-yellow-700">
                      Selon nos études, 67% des organisations sous-estiment l'ampleur de la transformation nécessaire, 
                      se contentant d'adaptations superficielles qui ne répondent pas aux enjeux stratégiques de long terme.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Innovative Solutions */}
            <div id="solutions" className="space-y-6">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Icon name="Lightbulb" size={24} className="text-green-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Solutions Innovantes et Méthodologies Éprouvées</h2>
              </div>

              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                <p className="mb-6">
                  Notre approche systémique du développement des compétences s'articule autour de trois piliers fondamentaux : l'analyse prédictive des besoins, la personnalisation des parcours d'apprentissage, et l'intégration de l'intelligence artificielle 
                  comme amplificateur de performance.
                </p>
              </div>

              {/* Solution Framework */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-8">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                    <Icon name="Search" size={24} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Analyse Prédictive</h3>
                  <p className="text-gray-700 text-sm">
                    Identification des compétences critiques basée sur l'évolution technologique et les besoins sectoriels futurs.
                  </p>
                </div>
                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                    <Icon name="User" size={24} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Personnalisation</h3>
                  <p className="text-gray-700 text-sm">
                    Parcours d'apprentissage adaptatifs tenant compte du profil, du rythme et des objectifs individuels.
                  </p>
                </div>
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
                    <Icon name="Zap" size={24} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">IA Intégrée</h3>
                  <p className="text-gray-700 text-sm">
                    Utilisation de l'intelligence artificielle pour optimiser l'acquisition de connaissances et mesurer les progrès.
                  </p>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-4">Prêt à Transformer Votre Organisation ?</h3>
              <p className="text-blue-100 mb-6 text-lg">
                Découvrez comment notre méthodologie peut révolutionner le développement des compétences dans votre entreprise.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200">
                  Évaluation Gratuite
                </button>
                <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-200">
                  Parler à un Expert
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArticleContent;